import moment from 'moment';
import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import {
  DISPUTE__RESET,
  DISPUTE__MUTATE_LOADING_STATUS,
  DISPUTE__MUTATE_DISPUTABLE_SCHEDULE_EVENTS,
  DISPUTE__MUTATE_RELEVANT_SCHEDULES,
  DISPUTE__MUTATE_CURRENT_DISPUTE,
  DISPUTE__GET_DISPUTABLE_SCHEDULE_EVENTS,
  DISPUTE__OPEN_SUPPORT_TICKET,
  DISPUTE__SUBMIT_DISPUTE,
  DISPUTE__REQUEST_FIX_OR_CREDIT,
  DISPUTES_REDISTRIBUTE,
  DISPUTE__GET_RELEVANT_SCHEDULES,
  DISPUTE__REPORT_EARLY_COMPLETION,
  DISPUTE__SWITCH_CREW,
  DISPUTE__INITIALIZE_CURRENT_DISPUTE,
  DISPUTE__GOTO_INITIAL_STEP,
  DISPUTE__GOTO_RESOLUTION_STEP,
  DISPUTE__UPDATE_CURRENT_DISPUTE,
  DISPUTE__PROPERTIES_WITH_DISPUTABLE_SCHEDULE_EVENTS,
  DISPUTE__DECLINE_FIX,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';
import router from '@/router';
import { lsDisputeStoreHelpers } from './dispute.helpers';

const createInitialState = () => {
  return {
    disputableScheduleEvents: [], // organized by property
    relevantSchedules: [],
    currentDispute: {},
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [DISPUTE__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [DISPUTE__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },

  [DISPUTE__MUTATE_DISPUTABLE_SCHEDULE_EVENTS](state, payload) {
    state.disputableScheduleEvents = [...payload];
  },

  [DISPUTE__MUTATE_RELEVANT_SCHEDULES](state, payload) {
    state.relevantSchedules = [...payload];
  },

  [DISPUTE__MUTATE_CURRENT_DISPUTE](state, payload) {
    state.currentDispute = {
      ...state.currentDispute,
      ...payload,
    };
  },
};

export const actions = {
  [DISPUTE__GET_DISPUTABLE_SCHEDULE_EVENTS]({ commit, rootState }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'GET',
        route: `/customers/${customer_id}/scheduleevents/disputable`,
        include: ['images'],
      })
      .then(({ data }) => {
        commit(DISPUTE__MUTATE_DISPUTABLE_SCHEDULE_EVENTS, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  [DISPUTE__OPEN_SUPPORT_TICKET]({ commit, state }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { notes, schedule_id, otherScheduleID, images } = state.currentDispute;

    let support_type = null;

    const payload = lsDisputeStoreHelpers.formatDisputePayload({
      formData: {
        schedule_id: otherScheduleID || schedule_id,
        support_type,
        message: notes,
        images,
      },
    });

    return lsApiService
      .request({
        method: 'POST',
        route: '/support',
        payload,
      })
      .then(() => {
        commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
          currentStep: 'ReturnRequestSuccess',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [DISPUTE__SUBMIT_DISPUTE]({ commit, dispatch, state, rootState }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const {
      property_id,
      schedule_id,
      scheduleevent_id,
      dispute_type,
      reason,
      full_yard,
      front_yard,
      left_yard,
      back_yard,
      right_yard,
      other_yard,
      notes,
      images,
    } = state.currentDispute;

    const payload = lsDisputeStoreHelpers.formatDisputePayload({
      formData: {
        dispute_type,
        reason,
        full_yard: Boolean(full_yard),
        front_yard: Boolean(front_yard),
        left_yard: Boolean(left_yard),
        back_yard: Boolean(back_yard),
        right_yard: Boolean(right_yard),
        other_yard: Boolean(other_yard),
        notes,
        images,
      },
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/disputes/completions`,
        payload,
      })
      .then(({ data }) => {
        commit(DISPUTE__MUTATE_CURRENT_DISPUTE, data);

        // if contractor submitted a partial completion and the reason is not "other"
        const partialCompleteReason = _.get(data, 'schedule_event_partial_completion.reason');
        if (
          (dispute_type === 'partial_complete' || dispute_type === 'no_work_done') &&
          partialCompleteReason &&
          partialCompleteReason !== 'other'
        ) {
          commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
            currentStep: 'ReturnRequestPartialCompleteReminder',
          });
        } else {
          dispatch(DISPUTE__GOTO_RESOLUTION_STEP);
        }
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [DISPUTE__REQUEST_FIX_OR_CREDIT]({ commit, rootState }, { type }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const {
      property_id,
      schedule_id,
      scheduleevent_id,
      dispute_completion_id,
    } = rootState.dispute.currentDispute;
    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/disputes/completions/${dispute_completion_id}/${type}`,
      })
      .then(({ data }) => {
        if (type === 'fix') {
          commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
            currentStep: 'ReturnRequestFixRequested',
          });
        }

        if (type === 'credit' && _.get(data, 'redistribution_offered_at')) {
          commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
            currentStep: 'ReturnRequestRedistribute',
          });
        } else if (type === 'credit') {
          commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
            currentStep: 'ReturnRequestCreditApplied',
          });
        }
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [DISPUTES_REDISTRIBUTE]({ commit, rootState }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const {
      property_id,
      schedule_id,
      scheduleevent_id,
      dispute_completion_id,
    } = rootState.dispute.currentDispute;
    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/disputes/completions/${dispute_completion_id}/redistribute`,
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  // will be used once FRONT-7998 is complete
  [DISPUTE__GET_RELEVANT_SCHEDULES]({ commit, rootState }, { property_id, schedule_id }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/relevant-schedules`,
        payload: {
          before: moment()
            .add(1, 'week')
            .format('YYYY-MM-DD'),
          after: moment()
            .subtract(2, 'weeks')
            .format('YYYY-MM-DD'),
          exclude_schedule_ids: [schedule_id],
        },
      })
      .then(({ data }) => {
        commit(DISPUTE__MUTATE_RELEVANT_SCHEDULES, data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },

  // will be used once FRONT-7997 is complete
  [DISPUTE__REPORT_EARLY_COMPLETION](
    { commit, rootState },
    { property_id, schedule_id, scheduleevent_id }
  ) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/early`,
      })
      .then(_.noop)
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  // will be used once FRONT-7997 is complete
  [DISPUTE__SWITCH_CREW](
    { commit, rootState },
    { property_id, schedule_id, scheduleevent_id, scheduleevent_review_id }
  ) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/reviews/${scheduleevent_review_id}/redistribute`,
      })
      .then(_.noop)
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [DISPUTE__INITIALIZE_CURRENT_DISPUTE](
    { rootState, commit, dispatch },
    {
      property_id,
      schedule_id,
      scheduleevent_id,
      scheduleevent_review_id,
      schedule_event,
      dispute_type,
      redistributionOffered,
    }
  ) {
    commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
      property_id,
      schedule_id,
      scheduleevent_id,
      scheduleevent_review_id,
      schedule_event,
      dispute_type,
      redistributionOffered,
    });

    if (
      lsDisputeStoreHelpers.willAutoRedistributeFix({
        flags: rootState.featureFlags.flags,
        dispute_type,
        schedule_event,
      })
    ) {
      commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
        currentStep: 'ReturnRequestSecondReview',
      });
    } else {
      dispatch(DISPUTE__GOTO_INITIAL_STEP);
    }

    router.push({
      name: 'returnRequest',
    });
  },

  [DISPUTE__GOTO_INITIAL_STEP]({ state, commit }) {
    const { dispute_type: disputeType } = state.currentDispute;

    const disputeLabels = {
      partial_complete: 'ReturnRequestLocation',
      quality: 'ReturnRequestQualityIssue',
      no_work_done: 'ReturnRequestPhotos',
    };

    const currentStep = disputeLabels[disputeType];

    commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
      currentStep,
    });
  },

  [DISPUTE__GOTO_RESOLUTION_STEP]({ commit, state }) {
    const { credit_offered_at, credit_offered_amount } = state.currentDispute;

    if (credit_offered_at && credit_offered_amount) {
      commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
        currentStep: 'ReturnRequestCreditOrFix',
      });
    } else {
      commit(DISPUTE__MUTATE_CURRENT_DISPUTE, {
        currentStep: 'ReturnRequestConfirmFix',
      });
    }
  },

  [DISPUTE__UPDATE_CURRENT_DISPUTE]({ commit }, { ...payload }) {
    commit(DISPUTE__MUTATE_CURRENT_DISPUTE, payload);
  },

  [DISPUTE__DECLINE_FIX]({ commit, rootState }, { onSuccess = _.noop }) {
    commit(DISPUTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const {
      property_id,
      schedule_id,
      scheduleevent_id,
      dispute_completion_id,
    } = rootState.dispute.currentDispute;
    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/disputes/completions/${dispute_completion_id}/decline-fix`,
      })
      .then(() => {
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(DISPUTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {
  [DISPUTE__PROPERTIES_WITH_DISPUTABLE_SCHEDULE_EVENTS](state, getters, rootState) {
    const { disputableScheduleEvents } = rootState.dispute;
    const { propertiesById } = rootState.properties;

    const sections = _.map(disputableScheduleEvents, ({ property_id, street, schedules }) => {
      return {
        key: `property_id:${property_id}`,
        property: {
          ...propertiesById[property_id],
        },

        data: _.flatMap(schedules, (schedule) => {
          return _.map(schedule.scheduleevents, (scheduleEvent) => {
            // Schedule is a parent of scheduleEvent and a child of scheduleEvent/
            // We want to remove any reference to the child schedule object.
            // To do this, we are destructuring scheduleevents to take the object out of schedule
            // and adding the parent schedule without the scheduleevents (scheduleWithoutScheduleEvents) to scheduleEvent
            const { scheduleevents, ...scheduleWithoutScheduleEvents } = schedule;
            return {
              ...scheduleEvent,
              schedule: { ...scheduleWithoutScheduleEvents },
              property: { property_id, street },
              key: `scheduleevent_id:${scheduleEvent.scheduleevent_id}`,
            };
          });
        }),
      };
    });

    return _.orderBy(
      sections,
      [
        (section) => {
          // prioritize active properties
          return section.property.is_active;
        },
        (section) => {
          // maintain property creation order in their respective is_active t/f buckets
          return section.property.property_id;
        },
      ],
      ['desc', 'asc']
    );
  },
};

export const dispute = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
